import type { Nullable, WikiPageTreeItemWithChildren } from "@msuite/utils/types";

/** Where a page sits in its space: the pages above it, root first, and its own tree node. */
export interface WikiPageLineage {
	ancestors: Array<WikiPageTreeItemWithChildren>;
	page: WikiPageTreeItemWithChildren | null;
}

const EMPTY: WikiPageLineage = { ancestors: [], page: null };

/**
 * Locates a page in the space's page tree and returns the path down to it. The header's trail
 * renders the ancestors and the sub-page list reads the node's children, so both sides of the
 * page derive from one lookup over the tree the sidebar already holds.
 *
 * @param pages - The space's page tree, as `useWikiSpacePages` returns it.
 * @param pageId - The page to locate.
 * @returns The ancestors root-first and the page's own node; both empty when the page is not in the tree.
 */
export function findPageLineage(
	pages: Nullable<Array<WikiPageTreeItemWithChildren>>,
	pageId: Nullable<string>,
): WikiPageLineage {
	if (!pages || !pageId) return EMPTY;
	const path = findPath(pages, pageId, []);
	if (!path) return EMPTY;
	return { ancestors: path.slice(0, -1), page: path[path.length - 1] ?? null };
}

function findPath(
	pages: Array<WikiPageTreeItemWithChildren>,
	pageId: string,
	trail: Array<WikiPageTreeItemWithChildren>,
): Array<WikiPageTreeItemWithChildren> | null {
	for (const page of pages) {
		const here = [...trail, page];
		if (page.id === pageId) return here;
		const below = findPath(page._children ?? [], pageId, here);
		if (below) return below;
	}
	return null;
}
